/**
 * Composer ADMIN — creazione newsletter (Sprint 18, RF-02/03/04 + §11).
 *
 * Flusso:
 *  - selezione entità mittente (gating NEWSLETTER W per-entità, §11.10)
 *  - nome mittente (sender_display_name, default short_name ?? name)
 *  - template (opzionale) → popola oggetto/corpo
 *  - editor HTML TipTap con placeholder + unsubscribe obbligatorio
 *  - destinatari XOR filtro/categoria + anteprima conteggio
 *  - reply-to verificato (+ OTP)
 *  - invio immediato (save bozza + send-now, DIP-6) o programmato
 *
 * Riferimento: TDD Sprint 18 §8.2–§8.8.
 */
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  Container,
  Grid,
  Box,
  Stack,
  Typography,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
//--------------------------------------------------------
import { CustomTextField, CustomAutocomplete } from '../../../utils/StyledInputs';
import { DefaultContainedButton, DefaultOutlinedButton } from '../../../utils/Buttons';
import { EntitiesDialog } from '../../../utils/EntitiesDialog';
import { HttpErrorHandler } from '../../../utils/ErrorHandler';
import { usePopup } from '../../../context/PopupContext';
import { useNewsletterAuth } from '../../../hooks/useNewsletterAuth';
import { newslettersApi } from '../../../services/newsletter/newslettersApi';
import { mediaApi } from '../../../services/newsletter/mediaApi';
import { templatesApi } from '../../../services/newsletter/templatesApi';
import { inlineEmailHtml, ensureUnsubscribePlaceholder } from '../../../utils/newsletter/emailHtml';
import { buildAttachmentFormData } from '../../../utils/newsletter/attachments';
import { HtmlEditor } from './components/HtmlEditor/HtmlEditor';
import { RecipientSelector } from './components/RecipientSelector';
import { ReplyToSelector } from './components/ReplyToSelector';
import { ScheduleControls } from './components/ScheduleControls';
import { AttachmentsCard } from './components/AttachmentsCard';
import { MediaLibraryDialog } from './components/MediaLibraryDialog';
import {
  composeScheduleDateTime,
  isScheduleTooSoon,
  scheduleTooSoonMessage,
} from '../../../utils/newsletter/schedule';

dayjs.locale('it');

/** True se l'HTML dell'editor è vuoto (solo markup senza testo/immagini). */
function isHtmlEmpty(html) {
  if (!html) return true;
  const withoutTags = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim();
  return withoutTags.length === 0 && !/<img|<hr|<br/i.test(html);
}

export default function NewNewsletter() {
  const navigate = useNavigate();
  const { showPopup, setPopUpText } = usePopup();

  const [currentProfile, setCurrentProfile] = useState('');

  // Entità mittente
  const [openEntitiesDialog, setOpenEntitiesDialog] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const { canWrite } = useNewsletterAuth(selectedEntity);

  // Dati newsletter
  const [name, setName] = useState('');
  const [senderDisplayName, setSenderDisplayName] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  // Template
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateAttachments, setTemplateAttachments] = useState([]); // ereditati dal template (preview)

  // Allegati aggiunti alla newsletter (caricati dopo la create)
  const [stagedFiles, setStagedFiles] = useState([]);
  // File scelti dalla libreria: già su S3, al salvataggio si crea solo il collegamento.
  const [stagedMedia, setStagedMedia] = useState([]);
  const [mediaOpen, setMediaOpen] = useState(false);

  // Destinatari (XOR)
  const [recipientMode, setRecipientMode] = useState('filter');
  const [filterId, setFilterId] = useState(null);
  const [recipientCategory, setRecipientCategory] = useState(null);

  // Reply-to
  const [replyToEmailId, setReplyToEmailId] = useState(null);

  // Invio / programmazione
  const [sendMode, setSendMode] = useState('now');
  const [sendingDate, setSendingDate] = useState(null);
  const [sendingTime, setSendingTime] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Profilo corrente (guard ADMIN)
  useEffect(() => {
    let data;
    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));
    if (data) setCurrentProfile(data.roles.filter((role) => role.last_used));
  }, []);

  // Default sender_display_name al cambio entità.
  useEffect(() => {
    if (selectedEntity) setSenderDisplayName(selectedEntity.short_name ?? selectedEntity.name ?? '');
  }, [selectedEntity]);

  // Carica i template disponibili per l'entità (di entità + standard) al cambio entità.
  useEffect(() => {
    if (!selectedEntity?.id) {
      setTemplates([]);
      return;
    }
    templatesApi
      .list({ is_archived: false })
      .then((res) => {
        const all = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
        // standard (entity_id null) + template della stessa entità
        setTemplates(all.filter((t) => !t.entity_id || t.entity_id === selectedEntity.id));
      })
      .catch((err) => {
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    if (!template) {
      setTemplateAttachments([]);
      return;
    }
    // Popola oggetto/corpo dal template selezionato e mostra i suoi allegati (ereditati).
    templatesApi
      .get(template.id)
      .then((res) => {
        const t = res.data ?? {};
        if (t.subject) setSubject(t.subject);
        if (t.body_html) setBodyHtml(t.body_html);
        setTemplateAttachments(Array.isArray(t.attachments) ? t.attachments : []);
      })
      .catch((err) => {
        HttpErrorHandler(err, setPopUpText);
        showPopup(false);
      });
  };

  // Allegati aggiuntivi (staged, caricati dopo la create).
  const handleFilesPicked = ({ valid, rejected }) => {
    if (valid.length) setStagedFiles((prev) => [...prev, ...valid]);
    if (rejected.length) showPopup('warning', rejected.join(' · '));
  };
  const handleRemoveStaged = (index) => setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  const handleRemoveStagedMedia = (index) => setStagedMedia((prev) => prev.filter((_, i) => i !== index));

  // Stesso file scelto due volte: si tiene una sola voce.
  const handlePickMedia = (media) =>
    setStagedMedia((prev) => (prev.some((m) => m.id === media.id) ? prev : [...prev, media]));

  /**
   * Validazione in base all'intento:
   *  - 'draft'             → minima: serve solo l'entità mittente e il nome interno.
   *  - 'send' | 'schedule' → completa: oggetto, corpo, destinatari (XOR) e, se schedule, data/ora.
   * Il placeholder {{unsubscribe_url}} NON è richiesto all'utente: viene iniettato
   * automaticamente nel payload (vedi buildPayload), essendo obbligatorio lato BE.
   */
  const validate = (intent) => {
    const e = {};
    if (!selectedEntity?.id) e.entity = true;
    if (!name.trim()) e.name = true;

    if (intent !== 'draft') {
      if (!subject.trim()) e.subject = true;
      if (isHtmlEmpty(bodyHtml)) e.body = true;
      // XOR destinatari
      const hasFilter = recipientMode === 'filter' && !!filterId;
      const hasCategory = recipientMode === 'category' && !!recipientCategory;
      if (!hasFilter && !hasCategory) e.recipients = true;
      // reply-to obbligatorio per invio/programmazione (facoltativo in bozza)
      if (!replyToEmailId) e.replyTo = true;
      // programmazione
      if (intent === 'schedule') {
        if (!sendingDate) e.date = true;
        if (!sendingTime) e.time = true;
        // L'orario si può anche digitare, quindi il limite del campo non basta.
        if (sendingDate && sendingTime && isScheduleTooSoon(sendingDate, sendingTime)) {
          e.time = true;
          e.scheduleTooSoon = true;
        }
      }
    }

    setErrors(e);
    return e;
  };

  const buildPayload = (intent) => {
    const payload = {
      entity_id: selectedEntity.id,
      filter_id: recipientMode === 'filter' ? filterId : null,
      recipient_category: recipientMode === 'category' ? recipientCategory : null,
      template_id: selectedTemplate ? selectedTemplate.id : null,
      reply_to_email_id: replyToEmailId,
      sender_display_name: senderDisplayName?.trim() ? senderDisplayName.trim() : null,
      name: name.trim(),
      subject: subject.trim(),
      // L'unsubscribe è obbligatorio lato BE ma gestito automaticamente: lo iniettiamo se assente.
      body_html: inlineEmailHtml(ensureUnsubscribePlaceholder(bodyHtml)),
      scheduled_at: null,
    };
    // scheduled_at solo per la programmazione: bozza e invio immediato restano senza.
    if (intent === 'schedule' && sendingDate && sendingTime) {
      payload.scheduled_at = composeScheduleDateTime(sendingDate, sendingTime).toISOString();
    }
    return payload;
  };

  /**
   * Esegue l'azione richiesta.
   * @param {'draft'|'send'|'schedule'} intent
   *  - 'draft'    → POST /newsletters senza scheduled_at → resta in DRAFT.
   *  - 'schedule' → POST /newsletters con scheduled_at → SCHEDULED.
   *  - 'send'     → POST /newsletters (DRAFT) + POST /send-now (DIP-6).
   */
  const submit = async (intent) => {
    const e1 = validate(intent);
    if (Object.keys(e1).length > 0) {
      // Orario troppo vicino: serve un messaggio specifico, altrimenti non si capisce
      // quale campo correggere.
      showPopup('warning', e1.scheduleTooSoon ? scheduleTooSoonMessage() : 'Inserisci i dati mancanti');
      return;
    }

    const payload = buildPayload(intent);
    setSubmitting(true);
    try {
      const res = await newslettersApi.create(payload);
      const id = res.data?.id ?? res.data?.newsletter?.id;

      // Carica gli allegati aggiunti (serve l'id, quindi dopo la create).
      // Gli allegati ereditati dal template vengono copiati dal BE alla creazione.
      if (id && stagedFiles.length > 0) {
        await Promise.all(stagedFiles.map((file) => newslettersApi.addAttachment(id, buildAttachmentFormData(file))));
      }

      // File di libreria: nessun ricaricamento, solo il collegamento alla newsletter.
      if (id && stagedMedia.length > 0) {
        await Promise.all(stagedMedia.map((media) => mediaApi.attachToNewsletter(id, media.id)));
      }

      if (intent === 'send') {
        // Invia ora (DIP-6): create + send-now (dopo l'upload degli allegati).
        if (!id) throw new Error('ID newsletter mancante nella risposta');
        await newslettersApi.sendNow(id);
        showPopup(true, 'Newsletter in invio');
      } else if (intent === 'schedule') {
        showPopup(true, 'Newsletter programmata');
      } else {
        showPopup(true, 'Bozza salvata');
      }
      setTimeout(() => navigate('/admin/newsletter'), 600);
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(sendMode === 'schedule' ? 'schedule' : 'send');
  };

  const handleSaveDraft = () => submit('draft');

  // Gestione errori applicativi specifici (oltre all'interceptor globale).
  const handleApiError = (err) => {
    const status = err?.response?.status;
    if (status === 422) {
      showPopup('warning', err.response?.data?.message || 'Dati non validi');
    } else if (status === 409) {
      showPopup(false, 'Operazione non consentita nello stato attuale');
    } else {
      HttpErrorHandler(err, setPopUpText);
      showPopup(false);
    }
  };

  if (currentProfile && currentProfile[0].prof_type_name !== 'ADMIN') return <Navigate to={{ pathname: '/' }} />;

  const showNoWriteWarning = selectedEntity && !canWrite;

  return (
    <>
      <Helmet>
        <title>Nuova Newsletter</title>
      </Helmet>

      <Container maxWidth="xl">
        <EntitiesDialog
          openDialog={openEntitiesDialog}
          closeDialog={() => setOpenEntitiesDialog(false)}
          outputEntity={(entity) => setSelectedEntity(entity)}
          context={'newsletter'}
        />

        <Stack spacing={3} component="form" onSubmit={handleSubmit}>
          {/* Mittente */}
          <Card sx={{ padding: '24px' }}>
            <CardHeader sx={{ padding: 0, mb: 2 }} title="Mittente" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {!selectedEntity ? (
                  <DefaultOutlinedButton
                    customStyles={{ width: '100%', height: '56px' }}
                    onClick={() => setOpenEntitiesDialog(true)}
                  >
                    Seleziona entità mittente *
                  </DefaultOutlinedButton>
                ) : (
                  <CustomTextField
                    label="Entità mittente *"
                    value={selectedEntity.name ?? ''}
                    error={!!errors.entity}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end" sx={{ cursor: 'pointer' }}>
                          <Tooltip title="Cambia entità" placement="left" arrow>
                            <EditIcon onClick={() => setOpenEntitiesDialog(true)} />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Nome mittente"
                  value={senderDisplayName}
                  disabled={!selectedEntity}
                  onChange={(e) => setSenderDisplayName(e.currentTarget.value)}
                  helperText="Mostrato come mittente; default = nome breve dell'entità"
                />
              </Grid>

              {showNoWriteWarning && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    Non hai i permessi per inviare newsletter per questa entità.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Card>

          {/* Destinatari */}
          <RecipientSelector
            entityId={selectedEntity?.id ?? null}
            mode={recipientMode}
            onModeChange={setRecipientMode}
            filterId={filterId}
            onFilterChange={setFilterId}
            category={recipientCategory}
            onCategoryChange={setRecipientCategory}
            disabled={!canWrite}
            error={!!errors.recipients}
          />

          {/* Contenuto */}
          <Card sx={{ padding: '24px' }}>
            <CardHeader sx={{ padding: 0, mb: 2 }} title="Contenuto" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <CustomTextField
                  label="Nome interno *"
                  value={name}
                  error={!!errors.name}
                  disabled={!canWrite}
                  onChange={(e) => setName(e.currentTarget.value)}
                  helperText="Identifica la newsletter nella lista (non inviato)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  id="newsletter-template"
                  disabled={!canWrite || !selectedEntity}
                  options={templates}
                  value={selectedTemplate}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name ?? ''}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      {option.name}
                      {!option.entity_id ? ' (standard)' : ''}
                    </Box>
                  )}
                  onChange={(_e, value) => handleSelectTemplate(value)}
                  renderInput={(params) => (
                    <CustomTextField {...params} label="Template (opzionale)" variant="outlined" />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextField
                  fullWidth
                  label="Oggetto *"
                  value={subject}
                  error={!!errors.subject}
                  disabled={!canWrite}
                  onChange={(e) => setSubject(e.currentTarget.value)}
                  helperText="Supporta i placeholder (es. {{nome}})"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Corpo della newsletter *
                </Typography>
                <HtmlEditor
                  value={bodyHtml}
                  onChange={setBodyHtml}
                  disabled={!canWrite}
                  entityId={selectedEntity?.id ?? null}
                  error={!!errors.body}
                  helperText={
                    'Usa "Inserisci campo" per i placeholder dinamici. Il link di disiscrizione viene aggiunto automaticamente.'
                  }
                />
              </Grid>
            </Grid>
          </Card>

          {/* Reply-to */}
          <Card sx={{ padding: '24px' }}>
            <CardHeader sx={{ padding: 0, mb: 2 }} title="Indirizzo di risposta (Reply-To)" />
            <ReplyToSelector
              entityId={selectedEntity?.id ?? null}
              value={replyToEmailId}
              onChange={setReplyToEmailId}
              canWrite={canWrite}
              disabled={!canWrite}
              error={!!errors.replyTo}
            />
            {errors.replyTo && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                Seleziona un indirizzo di risposta (obbligatorio per l&apos;invio).
              </Typography>
            )}
          </Card>

          {/* Allegati */}
          <AttachmentsCard
            helperText={
              templateAttachments.length > 0
                ? 'Gli allegati del template sono inclusi. Puoi aggiungerne altri. Max 10MB per file.'
                : 'PDF, immagini o documenti Office. Max 10MB per file.'
            }
            inherited={templateAttachments}
            staged={stagedFiles}
            onFilesPicked={handleFilesPicked}
            onRemoveStaged={handleRemoveStaged}
            library={stagedMedia}
            onRemoveLibrary={handleRemoveStagedMedia}
            onPickFromLibrary={() => setMediaOpen(true)}
            disabled={!canWrite}
          />

          <MediaLibraryDialog
            open={mediaOpen}
            mode="all"
            entityId={selectedEntity?.id ?? null}
            onClose={() => setMediaOpen(false)}
            onSelectMedia={handlePickMedia}
          />

          {/* Invio / programmazione */}
          <ScheduleControls
            mode={sendMode}
            onModeChange={setSendMode}
            date={sendingDate}
            time={sendingTime}
            onDateChange={setSendingDate}
            onTimeChange={setSendingTime}
            disabled={!canWrite}
            errors={{ date: errors.date, time: errors.time }}
          />

          {/* Azioni */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <DefaultOutlinedButton onClick={() => navigate('/admin/newsletter')} disabled={submitting}>
              Annulla
            </DefaultOutlinedButton>
            <DefaultOutlinedButton type="button" onClick={handleSaveDraft} disabled={!canWrite || submitting}>
              Salva come bozza
            </DefaultOutlinedButton>
            <DefaultContainedButton type="submit" disabled={!canWrite || submitting}>
              {sendMode === 'schedule' ? 'Programma invio' : 'Invia ora'}
            </DefaultContainedButton>
          </Box>
        </Stack>
      </Container>
    </>
  );
}
